<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  describeRRule,
  formatRRule,
  parseRRule,
  weekdayLabel,
  WEEKDAYS,
  type Frequency,
  type RepeatRule,
  type Weekday,
} from '@/todos/rrule'

const { t } = useI18n()
const props = defineProps<{ modelValue: string | null; disabled?: boolean; hasDate: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [value: string | null] }>()

const rule = ref<RepeatRule | null>(null)
const endMode = ref<'never' | 'until' | 'count'>('never')
const unparseable = computed(() => !!props.modelValue && parseRRule(props.modelValue) === null)

watch(
  () => props.modelValue,
  (value) => {
    rule.value = parseRRule(value)
    endMode.value = rule.value?.until ? 'until' : rule.value?.count ? 'count' : 'never'
  },
  { immediate: true },
)

function emitRule(): void {
  const text = formatRRule(rule.value)
  if (text !== props.modelValue) emit('update:modelValue', text)
}

function setFrequency(value: string): void {
  if (!value) {
    rule.value = null
  } else {
    rule.value = {
      ...(rule.value ?? { interval: 1, byday: [], until: null, count: null, extra: [] }),
      freq: value as Frequency,
    }
    if (rule.value.freq !== 'WEEKLY') rule.value.byday = []
  }
  emitRule()
}

function toggleDay(day: Weekday): void {
  if (!rule.value) return
  rule.value.byday = rule.value.byday.includes(day)
    ? rule.value.byday.filter((d) => d !== day)
    : [...rule.value.byday, day]
  emitRule()
}

function setEndMode(mode: 'never' | 'until' | 'count'): void {
  endMode.value = mode
  if (!rule.value) return
  rule.value.until =
    mode === 'until' ? (rule.value.until ?? new Date().toISOString().slice(0, 10)) : null
  rule.value.count = mode === 'count' ? (rule.value.count ?? 5) : null
  emitRule()
}
</script>

<template>
  <div class="rrule">
    <p v-if="unparseable" class="ui-hint">
      <i18n-t keypath="rrule.customFromApp">
        <template #rule
          ><code>{{ modelValue }}</code></template
        >
      </i18n-t>
    </p>
    <p v-if="!hasDate && rule" class="ui-hint">
      {{ t('rrule.needsDate') }}
    </p>
    <div class="line">
      <select
        class="ui-select"
        :value="rule?.freq ?? ''"
        :disabled="disabled"
        @change="setFrequency(($event.target as HTMLSelectElement).value)"
      >
        <option value="">{{ t('rrule.never') }}</option>
        <option value="DAILY">{{ t('rrule.daily') }}</option>
        <option value="WEEKLY">{{ t('rrule.weekly') }}</option>
        <option value="MONTHLY">{{ t('rrule.monthly') }}</option>
        <option value="YEARLY">{{ t('rrule.yearly') }}</option>
      </select>
      <template v-if="rule">
        <span class="ui-hint">{{ t('rrule.every') }}</span>
        <input
          v-model.number="rule.interval"
          type="number"
          min="1"
          max="999"
          class="ui-input interval"
          :disabled="disabled"
          @change="emitRule"
        />
      </template>
    </div>
    <div v-if="rule?.freq === 'WEEKLY'" class="days">
      <button
        v-for="day in WEEKDAYS"
        :key="day"
        type="button"
        class="ui-btn day"
        :class="{ 'ui-btn-primary': rule.byday.includes(day) }"
        :disabled="disabled"
        @click="toggleDay(day)"
      >
        {{ weekdayLabel(day) }}
      </button>
    </div>
    <div v-if="rule" class="line">
      <select
        class="ui-select"
        :value="endMode"
        :disabled="disabled"
        @change="
          setEndMode(($event.target as HTMLSelectElement).value as 'never' | 'until' | 'count')
        "
      >
        <option value="never">{{ t('rrule.forever') }}</option>
        <option value="until">{{ t('rrule.untilDate') }}</option>
        <option value="count">{{ t('rrule.numberOfTimes') }}</option>
      </select>
      <input
        v-if="endMode === 'until'"
        v-model="rule.until"
        type="date"
        class="ui-input"
        :disabled="disabled"
        @change="emitRule"
      />
      <input
        v-if="endMode === 'count'"
        v-model.number="rule.count"
        type="number"
        min="1"
        class="ui-input interval"
        :disabled="disabled"
        @change="emitRule"
      />
    </div>
    <p v-if="rule" class="ui-hint">
      {{ t('rrule.checkOffHint', { description: describeRRule(modelValue) }) }}
    </p>
  </div>
</template>

<style scoped>
.rrule {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.3rem 0;
}

.line {
  display: flex;
  gap: 0.45rem;
  align-items: center;
}

.interval {
  max-width: 5rem;
}

.days {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.day {
  padding: 0.3rem 0.5rem;
  font-size: 0.85rem;
}

p {
  margin: 0;
}
</style>
